import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ScanLine, ArrowRight, Maximize, Search, QrCode } from 'lucide-react';
import { Html5Qrcode, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { buildTraceabilityPath, extractBatchCodeFromQrPayload } from '../../utils/traceability-qr';

const QR_READER_ID = 'traceability-qr-reader';

const TraceabilitySearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [batchId, setBatchId] = useState('');
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isScanning, setIsScanning] = useState(false);
    const [scanError, setScanError] = useState('');
    const scannerRef = useRef(null);
    const scanHandledRef = useRef(false);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => () => {
        const scanner = scannerRef.current;
        scannerRef.current = null;
        if (!scanner) return;

        const stopPromise = scanner.isScanning ? scanner.stop() : Promise.resolve();
        stopPromise
            .catch(() => undefined)
            .finally(() => {
                try {
                    scanner.clear();
                } catch (error) {
                    // The reader element may already be unmounted.
                }
            });
    }, []);

    const stopScanner = async () => {
        const scanner = scannerRef.current;
        scannerRef.current = null;
        if (!scanner) return;

        if (scanner.isScanning) {
            await scanner.stop();
        }
        await scanner.clear();
        setIsScanning(false);
    };

    const openBatchTrace = (value) => {
        const path = buildTraceabilityPath(value);
        if (!path) return false;
        setBatchId(value);
        navigate(path);
        return true;
    };

    const handleQrScan = async (decodedText) => {
        if (scanHandledRef.current) return;

        const scannedBatchCode = extractBatchCodeFromQrPayload(decodedText);
        if (!scannedBatchCode) {
            setScanError('This QR code does not contain a valid batch code.');
            return;
        }

        scanHandledRef.current = true;
        setBatchId(scannedBatchCode);
        try {
            await stopScanner();
        } catch (error) {
            // Navigation should still continue after a successful decode.
        }
        openBatchTrace(scannedBatchCode);
    };

    const startScanner = async () => {
        if (scannerRef.current) return;

        setScanError('');
        scanHandledRef.current = false;
        const scanner = new Html5Qrcode(QR_READER_ID, {
            formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
            verbose: false
        });
        scannerRef.current = scanner;

        try {
            await scanner.start(
                { facingMode: 'environment' },
                { fps: 10, qrbox: { width: 220, height: 220 } },
                handleQrScan,
                () => undefined
            );
            setIsScanning(true);
        } catch (error) {
            scannerRef.current = null;
            try {
                scanner.clear();
            } catch (clearError) {
                // The scanner may not have completed initialization.
            }
            setIsScanning(false);
            setScanError('Camera access failed. Allow camera permission and try again.');
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const trimmedBatchId = batchId.trim();
        if (!trimmedBatchId) return;
        openBatchTrace(trimmedBatchId);
    };

    const hasValue = batchId.trim().length > 0;

    /* ══════════════════════════════════════════════════════════
       MOBILE VIEW — Figma 578:5982 (empty) & 578:6023 (filled)
       ══════════════════════════════════════════════════════════ */
    if (isMobile) {
        return (
            <div className="trace-search-wrapper mobile">
                <div className="trace-search-card mobile">
                    {/* Logo */}
                    <div className="trace-search-logo-section">
                        <ScanLine size={28} color="#071422" />
                        <span>
                            Syn<span className="green">Trace</span>
                        </span>
                    </div>

                    {/* Title & Description */}
                    <div className="trace-search-intro mobile">
                        <h1 className="mobile">
                            Scan QR Code
                        </h1>
                        <p>
                            Quickly trace the complete immutable history of your product by scanning the QR code
                        </p>
                    </div>

                    {/* QR Scanner Area */}
                    <div className="trace-qr-scanner-section">
                        <div className={`qr-container ${isScanning ? 'scanning' : ''}`}>
                            <div id={QR_READER_ID} className="qr-reader"></div>
                            {!isScanning && (
                                <button type="button" className="qr-start-button" onClick={startScanner}>
                                    <ScanLine size={34} />
                                    <span>Tap to Scan</span>
                                </button>
                            )}
                            {!isScanning && (
                                <div className="qr-overlay">
                                    <Maximize size={290} color="#02C170" strokeWidth={0.4} />
                                </div>
                            )}
                        </div>
                    </div>
                    {scanError && <div className="trace-qr-error">{scanError}</div>}

                    {/* Divider */}
                    <div className="trace-search-divider">
                        <div className="line"></div>
                        <span>Or Enter the Batch Code</span>
                        <div className="line"></div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSearch}>
                        <div className="trace-search-input-group">
                            <div className="search-icon">
                                <Search size={20} color="#90A1B9" />
                            </div>
                            <input 
                                type="text" 
                                placeholder="Enter Batch ID (e.g. BAT0001...)"
                                value={batchId}
                                onChange={(e) => setBatchId(e.target.value)}
                                className={hasValue ? 'has-value' : ''}
                            />
                        </div>

                        <button 
                            type="submit" 
                            disabled={!hasValue}
                            className={`trace-search-submit-btn mobile`}
                        >
                            Verify Traceability
                            <ArrowRight size={18} />
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    /* ══════════════════════════════════════════════════════════
       DESKTOP VIEW — Figma 630:7121 (empty) & 630:7162 (filled)
       ══════════════════════════════════════════════════════════ */
    return (
        <div className="trace-search-wrapper desktop">
            {/* White Card */}
            <div className="trace-search-card desktop">
                {/* Top Section - Icon + Title */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px', width: '330px' }}>
                    {/* Green QR Icon */}
                    <div className="trace-desktop-search-icon-box">
                        <QrCode size={40} color="#FFFFFF" strokeWidth={1.8} />
                    </div>

                    {/* Title & Description */}
                    <div className="trace-search-intro desktop">
                        <h1 className="desktop">
                            Trace Your Product
                        </h1>
                        <p>
                            Scan a QR code or enter a Batch ID to view the complete immutable history of your product.
                        </p>
                    </div>
                </div>

                {/* Form Container */}
                <div className="trace-search-form-container desktop-card">
                    <form onSubmit={handleSearch} style={{ width: '100%' }}>
                        <div className="trace-search-input-group">
                            <div className="search-icon">
                                <Search size={20} color="#90A1B9" />
                            </div>
                            <input 
                                type="text" 
                                placeholder="Enter Batch ID (e.g. BAT0001...)"
                                value={batchId}
                                onChange={(e) => setBatchId(e.target.value)}
                                className={hasValue ? 'has-value' : ''}
                            />
                        </div>

                        <button 
                            type="submit" 
                            disabled={!hasValue}
                            className="trace-search-submit-btn desktop"
                        >
                            Verify Traceability
                            <ArrowRight size={18} />
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default TraceabilitySearch;
